import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { reduxForm, change, formValueSelector, destroy } from "redux-form";
import { Callout, Icon, Intent, Tab, Tabs } from "@blueprintjs/core";
import immer from "immer";
import "./UploadCsvWizard.css";
import { isFunction } from "lodash-es";
import { compose } from "recompose";
import SimpleStepViz from "./SimpleStepViz";
import shortid from "shortid";
import { some } from "lodash-es";
import { times } from "lodash-es";
import DialogFooter from "./DialogFooter";
import DataTable from "./DataTable";
import { removeCleanRows } from "./DataTable/utils";
import wrapDialog from "./wrapDialog";
import { omit } from "lodash-es";
import { useDispatch, useSelector } from "react-redux";
import { MatchHeaders } from "./MatchHeaders";
import { isEmpty } from "lodash-es";
import { addSpecialPropToAsyncErrs } from "./FormComponents/tryToMatchSchemas";
import { cloneDeep } from "lodash-es";
import { InputField } from "./FormComponents";
import { useDeepEqualMemoIgnoreFns } from "./utils/hooks/useDeepEqualMemoIgnoreFns";
const asyncValidateHelper = async (validateAgainstSchema, currentEnts, changeForm, tableName) => {
  if (!validateAgainstSchema.tableWideAsyncValidation) return;
  const res = await validateAgainstSchema.tableWideAsyncValidation({
    entities: currentEnts
  });
  if (!isEmpty(res)) {
    changeForm(tableName, "reduxFormCellValidation", {
      ...addSpecialPropToAsyncErrs(res)
    });
    return true;
  }
};
const maybeStripIdFromEntities = (ents, validateAgainstSchema) => {
  let toRet;
  if (validateAgainstSchema?.fields?.some(({ path }) => path === "id")) {
    toRet = ents;
  } else {
    toRet = ents?.map((e) => omit(e, ["id"]));
  }
  return toRet?.map((e) => omit(e, ["_isClean"]));
};
const exampleData = { userData: times(5).map(() => ({ _isClean: true })) };
const getInitialSteps = (csvValidationIssue) => [
  { text: "Review Headers", active: csvValidationIssue },
  { text: "Review Data", active: !csvValidationIssue }
];
export const PreviewCsvData = (props) => {
  const {
    matchedHeaders,
    isEditingExistingFile,
    showDoesDataLookCorrectMsg,
    headerMessage,
    datatableFormName = "editableCellTable",
    validateAgainstSchema,
    userSchema = exampleData,
    entities
  } = props;
  const data = useMemo(() => {
    return userSchema.userData && userSchema.userData.length && userSchema.userData.map((row, i) => {
      const toRet = {
        _isClean: row._isClean
      };
      validateAgainstSchema.fields.forEach(({ path, defaultValue }) => {
        const matchingKey = matchedHeaders?.[path];
        if (!matchingKey) {
          toRet[path] = defaultValue === void 0 ? defaultValue : "";
        } else {
          toRet[path] = row[matchingKey];
        }
        if (toRet[path] === void 0 || toRet[path] === "") {
          if (defaultValue) {
            if (isFunction(defaultValue)) {
              toRet[path] = defaultValue(i, row);
            } else toRet[path] = defaultValue;
          } else {
            toRet[path] = "";
          }
        }
      });
      if (row.id === void 0) {
        toRet.id = shortid();
      } else {
        toRet.id = row.id;
      }
      return toRet;
    });
  }, [matchedHeaders, userSchema, validateAgainstSchema.fields]);
  return /* @__PURE__ */ jsxs("div", { style: { minWidth: 400 }, children: [
    /* @__PURE__ */ jsx(Callout, { style: { marginBottom: 5 }, intent: "primary", children: headerMessage || (showDoesDataLookCorrectMsg ? "Does this data look correct? Edit it as needed." : `${isEditingExistingFile ? "Edit" : "Input"} your data here. Hover table headers for additional instructions.`) }),
    validateAgainstSchema.description && /* @__PURE__ */ jsx(Callout, { children: validateAgainstSchema.description }),
    /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start"
        },
        children: validateAgainstSchema.HeaderComp && /* @__PURE__ */ jsx(validateAgainstSchema.HeaderComp, { ...props })
      }
    ),
    /* @__PURE__ */ jsx(
      DataTable,
      {
        maxWidth: 800,
        maxHeight: 500,
        destroyOnUnmount: false,
        doNotValidateUntouchedRows: true,
        formName: datatableFormName,
        isSimple: true,
        isCellEditable: true,
        entities: (entities ? entities : data) || [],
        schema: validateAgainstSchema
      }
    )
  ] });
};
export const SimpleInsertDataDialog = compose(
  wrapDialog({
    canEscapeKeyClose: false,
    title: "Build CSV File",
    style: { width: "fit-content" }
  }),
  reduxForm({ form: "SimpleInsertDataDialog" })
)(({
  dataTableForm = "simpleInsertEditableTable",
  entities,
  handleSubmit,
  headerMessage,
  isEditingExistingFile,
  matchedHeaders,
  onSimpleInsertDialogFinish,
  showDoesDataLookCorrectMsg,
  submitting,
  userSchema,
  validateAgainstSchema,
  initialValues
}) => {
  const dispatch = useDispatch();
  const _reduxFormEntities = useSelector(
    (state) => state.form?.[dataTableForm]?.values.reduxFormEntities
  );
  const reduxFormEntities = useDeepEqualMemoIgnoreFns(_reduxFormEntities);
  useEffect(() => {
    return () => dispatch(destroy(dataTableForm));
  }, [dataTableForm, dispatch]);
  const _reduxFormCellValidation = useSelector(
    function _reduxFormCellValidationSelector(state) {
      return state.form?.[dataTableForm]?.values.reduxFormCellValidation;
    }
  );
  const reduxFormCellValidation = useDeepEqualMemoIgnoreFns(
    _reduxFormCellValidation
  );
  const { entsToUse, validationToUse } = useMemo(
    () => removeCleanRows(reduxFormEntities, reduxFormCellValidation),
    [reduxFormEntities, reduxFormCellValidation]
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: "bp3-dialog-body", children: [
      /* @__PURE__ */ jsx(
        InputField,
        {
          isRequired: true,
          rightElement: /* @__PURE__ */ jsx("div", { style: { paddingTop: 6, paddingRight: 5 }, children: ".csv" }),
          inlineLabel: true,
          label: "File Name:",
          defaultValue: initialValues?.fileName ?? "manual_data_entry",
          name: "fileName"
        }
      ),
      /* @__PURE__ */ jsx(
        PreviewCsvData,
        {
          datatableFormName: dataTableForm,
          entities,
          headerMessage,
          isEditingExistingFile,
          matchedHeaders,
          showDoesDataLookCorrectMsg,
          userSchema,
          validateAgainstSchema
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      DialogFooter,
      {
        submitting,
        onClick: handleSubmit(async ({ fileName }) => {
          if (some(validationToUse, (e) => e)) return;
          if (await asyncValidateHelper(
            validateAgainstSchema,
            entsToUse,
            (...args) => dispatch(change(...args)),
            "simpleInsertEditableTable"
          ))
            return;
          onSimpleInsertDialogFinish({
            fileName: fileName + ".csv",
            newEntities: maybeStripIdFromEntities(
              entsToUse,
              validateAgainstSchema
            )
          });
        }),
        disabled: !entsToUse?.length || some(validationToUse, (e) => e),
        text: isEditingExistingFile ? "Edit Data" : "Add File"
      }
    )
  ] });
});
const UploadCsvWizardDialogInner = reduxForm()(({
  validateAgainstSchema,
  userSchema,
  searchResults,
  onUploadWizardFinish,
  csvValidationIssue,
  ignoredHeadersMsg,
  matchedHeaders,
  handleSubmit,
  fileIndex,
  onBackClick,
  changeForm,
  setFilesWIssues,
  doAllFilesHaveSameHeaders,
  filesWIssues,
  datatableFormName = "editableCellTable",
  onMultiFileUploadSubmit,
  isThisTheLastBadFile,
  submitting
}) => {
  const [hasSubmitted, setSubmitted] = useState(!csvValidationIssue);
  const [steps, setSteps] = useState(getInitialSteps(csvValidationIssue));
  const {
    reduxFormEntities: _reduxFormEntities,
    reduxFormCellValidation: _reduxFormCellValidation
  } = useSelector(function dtFormNameSelector(state) {
    return formValueSelector(datatableFormName)(
      state,
      "reduxFormEntities",
      "reduxFormCellValidation"
    );
  });
  const reduxFormEntities = useDeepEqualMemoIgnoreFns(_reduxFormEntities);
  const reduxFormCellValidation = useDeepEqualMemoIgnoreFns(
    _reduxFormCellValidation
  );
  let inner;
  if (hasSubmitted) {
    inner = /* @__PURE__ */ jsx(
      PreviewCsvData,
      {
        datatableFormName,
        showDoesDataLookCorrectMsg: true,
        entities: reduxFormEntities || null,
        matchedHeaders,
        validateAgainstSchema,
        userSchema
      }
    );
  } else {
    inner = /* @__PURE__ */ jsx(
      MatchHeaders,
      {
        onMultiFileUploadSubmit,
        csvValidationIssue,
        ignoredHeadersMsg,
        searchResults,
        matchedHeaders,
        userSchema,
        reduxFormEntitiesArray: reduxFormEntities ? [reduxFormEntities] : [],
        datatableFormName,
        setFilesWIssues,
        filesWIssues,
        fileIndex
      }
    );
  }
  const { entsToUse, validationToUse } = removeCleanRows(
    reduxFormEntities,
    reduxFormCellValidation
  );
  return /* @__PURE__ */ jsxs("div", { children: [
    !doAllFilesHaveSameHeaders && /* @__PURE__ */ jsx(SimpleStepViz, { style: { marginTop: 8 }, steps }),
    /* @__PURE__ */ jsx("div", { className: "bp3-dialog-body", children: inner }),
    /* @__PURE__ */ jsx(
      DialogFooter,
      {
        text: !hasSubmitted ? "Review and Edit Data" : onMultiFileUploadSubmit ? isThisTheLastBadFile ? "Finalize Files" : "Next File" : "Add File",
        submitting,
        disabled: hasSubmitted && (!entsToUse?.length || some(validationToUse, (v) => v)),
        intent: hasSubmitted && onMultiFileUploadSubmit && isThisTheLastBadFile ? Intent.SUCCESS : Intent.PRIMARY,
        noCancel: onMultiFileUploadSubmit,
        ...hasSubmitted && {
          onBackClick: onBackClick || (() => {
            setSteps(
              immer(steps, (draft) => {
                draft[0].active = true;
                draft[0].completed = false;
                draft[1].active = false;
              })
            );
            setSubmitted(false);
          })
        },
        onClick: handleSubmit(async () => {
          if (!hasSubmitted) {
            setSteps(
              immer(steps, (draft) => {
                draft[0].active = false;
                draft[0].completed = true;
                draft[1].active = true;
              })
            );
            setSubmitted(true);
            return;
          }
          if (!onMultiFileUploadSubmit) {
            if (await asyncValidateHelper(
              validateAgainstSchema,
              entsToUse,
              changeForm,
              datatableFormName
            ))
              return;
          }
          const payload = maybeStripIdFromEntities(
            entsToUse,
            validateAgainstSchema
          );
          return onMultiFileUploadSubmit ? await onMultiFileUploadSubmit() : onUploadWizardFinish({ res: [payload] });
        }),
        style: { alignSelf: "end" }
      }
    )
  ] });
});
const MultipleFileDialog = ({
  focusedTab,
  setFocusedTab,
  filesWIssues,
  finishedFiles,
  doAllFilesHaveSameHeaders,
  setSubmittedOuter,
  setSteps,
  reduxFormEntitiesArray,
  validateAgainstSchema,
  changeForm,
  onUploadWizardFinish,
  setFilesWIssues,
  csvValidationIssue,
  ignoredHeadersMsg,
  searchResults,
  matchedHeaders,
  userSchema,
  flippedMatchedHeaders,
  steps,
  hasSubmittedOuter
}) => {
  const tabs = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Callout, { style: { marginBottom: 10, flexGrow: 0 }, intent: "warning", children: /* @__PURE__ */ jsx("div", { children: "Please look over each of the following files and correct any issues." }) }),
    /* @__PURE__ */ jsx(
      Tabs,
      {
        selectedTabId: focusedTab,
        onChange: (i) => {
          setFocusedTab(i);
        },
        vertical: true,
        children: filesWIssues.map((f, i) => {
          const isGood = finishedFiles[i];
          const isThisTheLastBadFile = finishedFiles.every((ff, j) => {
            if (i === j) {
              return true;
            } else {
              return !!ff;
            }
          });
          return /* @__PURE__ */ jsx(
            Tab,
            {
              id: i,
              title: /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx(
                  Icon,
                  {
                    intent: isGood ? "success" : "warning",
                    icon: isGood ? "tick-circle" : "warning-sign"
                  }
                ),
                " ",
                f.file.name
              ] }),
              panel: /* @__PURE__ */ jsx(
                UploadCsvWizardDialogInner,
                {
                  isThisTheLastBadFile,
                  onBackClick: doAllFilesHaveSameHeaders && (() => {
                    setSubmittedOuter(false);
                    setSteps(getInitialSteps(true));
                  }),
                  onMultiFileUploadSubmit: async () => {
                    let nextUnfinishedFile;
                    for (let j = (i + 1) % finishedFiles.length; j < finishedFiles.length; j++) {
                      if (j === i) {
                        break;
                      } else if (!finishedFiles[j]) {
                        nextUnfinishedFile = j;
                        break;
                      } else if (j === finishedFiles.length - 1) {
                        j = -1;
                      }
                    }
                    if (nextUnfinishedFile !== void 0) {
                      const currentEnts = reduxFormEntitiesArray[focusedTab];
                      if (await asyncValidateHelper(
                        validateAgainstSchema,
                        currentEnts,
                        changeForm,
                        `editableCellTable-${focusedTab}`
                      ))
                        return;
                      setFocusedTab(nextUnfinishedFile);
                    } else {
                      for (const [i2, ents] of finishedFiles.entries()) {
                        if (await asyncValidateHelper(
                          validateAgainstSchema,
                          ents,
                          changeForm,
                          `editableCellTable-${i2}`
                        ))
                          return;
                      }
                      onUploadWizardFinish({
                        res: finishedFiles.map((ents) => {
                          return maybeStripIdFromEntities(
                            ents,
                            f.validateAgainstSchema
                          );
                        })
                      });
                    }
                  },
                  validateAgainstSchema,
                  reduxFormEntitiesArray,
                  filesWIssues,
                  finishedFiles,
                  onUploadWizardFinish,
                  doAllFilesHaveSameHeaders,
                  setFilesWIssues,
                  csvValidationIssue,
                  ignoredHeadersMsg,
                  searchResults,
                  matchedHeader: matchedHeaders,
                  userSchema,
                  flippedMatchedHeaders,
                  changeForm,
                  fileIndex: i,
                  form: `correctCSVHeadersForm-${i}`,
                  datatableFormName: `editableCellTable-${i}`,
                  ...f,
                  ...doAllFilesHaveSameHeaders && {
                    csvValidationIssue: false
                  }
                }
              )
            },
            i
          );
        })
      }
    )
  ] });
  const comp = doAllFilesHaveSameHeaders ? /* @__PURE__ */ jsxs(Fragment, { children: [
    doAllFilesHaveSameHeaders && /* @__PURE__ */ jsx(SimpleStepViz, { style: { marginTop: 8 }, steps }),
    !hasSubmittedOuter && /* @__PURE__ */ jsx(
      MatchHeaders,
      {
        doAllFilesHaveSameHeaders,
        datatableFormNames: filesWIssues.map((f, i) => {
          return `editableCellTable-${i}`;
        }),
        reduxFormEntitiesArray,
        csvValidationIssue,
        ignoredHeadersMsg,
        searchResults,
        matchedHeaders,
        userSchema,
        flippedMatchedHeaders,
        setFilesWIssues,
        filesWIssues,
        fileIndex: 0,
        ...filesWIssues[0]
      }
    ),
    hasSubmittedOuter && tabs,
    !hasSubmittedOuter && /* @__PURE__ */ jsx(
      DialogFooter,
      {
        style: { marginTop: 20 },
        onClick: () => {
          setSubmittedOuter(true);
          setSteps(getInitialSteps(false));
        },
        text: "Review and Edit Data"
      }
    )
  ] }) : tabs;
  return /* @__PURE__ */ jsx("div", { style: { padding: 10 }, children: comp });
};
const UploadCsvWizardDialog = compose(
  wrapDialog({
    canEscapeKeyClose: false,
    style: { width: "fit-content" }
  }),
  reduxForm({
    form: "UploadCsvWizardDialog"
  })
)(({
  csvValidationIssue,
  doAllFilesHaveSameHeaders,
  filesWIssues: _filesWIssues,
  flippedMatchedHeaders,
  ignoredHeadersMsg,
  matchedHeaders,
  onUploadWizardFinish,
  searchResults,
  userSchema,
  validateAgainstSchema
}) => {
  const dispatch = useDispatch();
  const changeForm = useCallback(
    (...args) => dispatch(change(...args)),
    [dispatch]
  );
  useEffect(() => {
    return () => {
      dispatch(
        destroy(
          "editableCellTable",
          ...times(_filesWIssues.length, (i) => `editableCellTable-${i}`)
        )
      );
    };
  }, [_filesWIssues.length, dispatch]);
  const { _reduxFormEntitiesArray, _finishedFiles } = useSelector((state) => {
    if (_filesWIssues.length > 0) {
      const reduxFormEntitiesArray2 = [];
      const finishedFiles2 = _filesWIssues.map((f, i) => {
        const { reduxFormEntities, reduxFormCellValidation } = formValueSelector(`editableCellTable-${i}`)(
          state,
          "reduxFormEntities",
          "reduxFormCellValidation"
        );
        reduxFormEntitiesArray2.push(reduxFormEntities);
        const { entsToUse, validationToUse } = removeCleanRows(
          reduxFormEntities,
          reduxFormCellValidation
        );
        return entsToUse && entsToUse.length && !some(validationToUse, (v) => v) && entsToUse;
      });
      return {
        _reduxFormEntitiesArray: reduxFormEntitiesArray2,
        _finishedFiles: finishedFiles2
      };
    }
  });
  const reduxFormEntitiesArray = useDeepEqualMemoIgnoreFns(
    _reduxFormEntitiesArray
  );
  const finishedFiles = useDeepEqualMemoIgnoreFns(_finishedFiles);
  const [hasSubmittedOuter, setSubmittedOuter] = useState();
  const [steps, setSteps] = useState(getInitialSteps(true));
  const [focusedTab, setFocusedTab] = useState(0);
  const [filesWIssues, setFilesWIssues] = useState(
    _filesWIssues.map(cloneDeep)
    //do this little trick to stop immer from preventing the file from being modified
  );
  if (filesWIssues.length > 1) {
    return /* @__PURE__ */ jsx(
      MultipleFileDialog,
      {
        focusedTab,
        setFocusedTab,
        filesWIssues,
        finishedFiles,
        doAllFilesHaveSameHeaders,
        setSubmittedOuter,
        setSteps,
        reduxFormEntitiesArray,
        validateAgainstSchema,
        changeForm,
        onUploadWizardFinish,
        setFilesWIssues,
        csvValidationIssue,
        ignoredHeadersMsg,
        searchResults,
        matchedHeaders,
        userSchema,
        flippedMatchedHeaders,
        steps,
        hasSubmittedOuter
      }
    );
  } else {
    return /* @__PURE__ */ jsx(
      UploadCsvWizardDialogInner,
      {
        form: "correctCSVHeadersForm",
        validateAgainstSchema,
        userSchema,
        searchResults,
        onUploadWizardFinish,
        csvValidationIssue,
        ignoredHeadersMsg,
        matchedHeaders,
        changeForm,
        setFilesWIssues,
        filesWIssues,
        flippedMatchedHeaders,
        fileIndex: 0,
        ...filesWIssues[0]
      }
    );
  }
});
export default UploadCsvWizardDialog;
